import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, DatePicker, Field, Modal, TextArea, TextInput } from '@bcecloud/portal-ui';
import { addDays, completeFollowUp, formatDay, type FollowUp, type RepWriteApplied } from '@/lib/sales-workspace';
import { WriteError } from './write-error';

/** The next follow-up defaults to the same subject, three days out. */
const DEFAULT_NEXT_IN_DAYS = 3;

export interface CompleteFollowUpDialogProps {
  /** The follow-up being completed; null closes the dialog. */
  readonly followUp: FollowUp | null;
  /** Today in BCE's time zone, `yyyy-mm-dd`. */
  readonly today: string;
  readonly onClose: () => void;
  /** After Salesforce took it (or a dry run): refresh what the page shows. */
  readonly onDone: (result: RepWriteApplied) => void;
  /** After a stale refusal: re-read so the rep sees the latest. */
  readonly onReload: () => void;
}

/**
 * "Complete and set next": marks the follow-up done and creates the next
 * one on the same deal, in one all-or-none write as the rep.
 */
export function CompleteFollowUpDialog({ followUp, today, onClose, onDone, onReload }: CompleteFollowUpDialogProps): React.ReactElement | null {
  if (!followUp) return null;
  // Keyed so each follow-up opens with its own defaults.
  return <CompleteFollowUpForm key={followUp.taskId} followUp={followUp} today={today} onClose={onClose} onDone={onDone} onReload={onReload} />;
}

function CompleteFollowUpForm({
  followUp,
  today,
  onClose,
  onDone,
  onReload,
}: CompleteFollowUpDialogProps & { readonly followUp: FollowUp }): React.ReactElement {
  const [subject, setSubject] = useState(followUp.subject);
  const [dueDate, setDueDate] = useState(addDays(today, DEFAULT_NEXT_IN_DAYS));
  const [notes, setNotes] = useState('');

  const complete = useMutation({
    mutationFn: () =>
      completeFollowUp(followUp.taskId, {
        expectedLastModified: followUp.lastModified,
        next: { subject: subject.trim(), dueDate, ...(notes.trim() ? { description: notes.trim() } : {}) },
      }),
    onSuccess: onDone,
  });

  const ready = subject.trim() !== '' && dueDate >= today;

  return (
    <Modal
      open
      title="Complete and set next"
      subtitle={`${followUp.opportunity.name} · ${followUp.subject}`}
      onClose={() => {
        if (!complete.isPending) onClose();
      }}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={complete.isPending}>
            Cancel
          </Button>
          <Button onClick={() => complete.mutate()} disabled={!ready || complete.isPending}>
            {complete.isPending ? 'Saving…' : 'Complete and set next'}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-portal-small text-portal-muted">
        This follow-up is marked done (it was due {formatDay(followUp.dueDate)}), and the next one is added to the deal.
      </p>
      <div className="grid gap-4">
        <Field label="Next follow-up" htmlFor="next-subject" required>
          <TextInput id="next-subject" value={subject} maxLength={255} onChange={(event) => setSubject(event.target.value)} />
        </Field>
        <Field label="Due" htmlFor="next-due" required>
          <DatePicker id="next-due" value={dueDate} min={today} onChange={setDueDate} />
        </Field>
        <Field label="Notes" htmlFor="next-notes">
          <TextArea id="next-notes" rows={3} value={notes} maxLength={4000} onChange={(event) => setNotes(event.target.value)} />
        </Field>
        {complete.isError ? <WriteError error={complete.error} onReload={onReload} /> : null}
      </div>
    </Modal>
  );
}
